import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Printer, Download, Loader2, AlertTriangle } from "lucide-react";
import { CONTRACT, UI_STRINGS } from "@/data/contractContent";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LANGS = [
  { code: "fr", label: "Français" },
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
];

export default function Contract() {
  const [lang, setLang] = useState("fr");
  const [downloading, setDownloading] = useState(false);
  const c = CONTRACT[lang];
  const t = UI_STRINGS[lang];

  const handlePrint = () => window.print();

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${API}/partners/contract/pdf?lang=${lang}`);
      if (!res.ok) throw new Error("bad status");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `contrat-partenaire-ifotolife-${lang}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF téléchargé.");
    } catch (e) {
      toast.error("Échec du téléchargement du PDF.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="contract-page">
      {/* Floating action bar */}
      <div className="no-print sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-[#E4E4E7]">
        <div className="mx-auto max-w-4xl px-5 md:px-8 h-16 flex items-center justify-between gap-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium text-[#52525B] hover:text-[#0A0A0A] transition-colors"
            data-testid="contract-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> {t.back}
          </Link>

          <div className="flex items-center gap-2">
            {/* Language switcher */}
            <div className="flex items-center bg-[#F4F4F5] rounded-full p-1" data-testid="lang-switcher">
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  onClick={() => setLang(l.code)}
                  className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wide rounded-full transition-all duration-200 ${
                    lang === l.code ? "bg-[#F97316] text-white" : "text-[#52525B] hover:text-[#0A0A0A]"
                  }`}
                  data-testid={`lang-btn-${l.code}`}
                >
                  {l.code}
                </button>
              ))}
            </div>

            <button
              onClick={handlePrint}
              className="inline-flex items-center gap-1.5 border border-[#E4E4E7] hover:border-[#0A0A0A] px-3.5 py-2 rounded-full text-sm font-medium transition-colors"
              data-testid="contract-print-button"
            >
              <Printer className="w-4 h-4" /> <span className="hidden sm:inline">{t.print}</span>
            </button>
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="inline-flex items-center gap-1.5 bg-[#F97316] hover:bg-[#EA580C] disabled:opacity-70 text-white px-3.5 py-2 rounded-full text-sm font-semibold transition-colors"
              data-testid="contract-download-button"
            >
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              <span className="hidden sm:inline">{downloading ? t.generating : t.download}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Document */}
      <motion.article
        key={lang}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="print-container mx-auto max-w-3xl bg-white my-8 md:my-14 px-6 sm:px-12 md:px-16 py-12 md:py-16 border border-[#E4E4E7] shadow-sm"
        data-testid="contract-document"
      >
        <div className="text-xs font-black uppercase tracking-label text-[#F97316]">{c.brand} · {t.docLabel}</div>
        <h1 className="mt-3 text-3xl md:text-4xl font-black tracking-tighter text-[#0A0A0A]">{c.doc_title}</h1>
        <p className="mt-2 text-base text-[#52525B] font-light">{c.doc_subtitle}</p>
        <p className="mt-1 text-xs text-[#A1A1AA] tracking-wide">{c.reference}</p>
        <div className="mt-5 h-[3px] w-14 bg-[#F97316]" />

        <p className="mt-6 text-[15px] leading-relaxed text-[#27272A]">{c.intro}</p>

        <div className="mt-6 flex gap-3 border-l-[3px] border-[#F97316] bg-[#FFF7ED] p-4" data-testid="lawyer-notice">
          <AlertTriangle className="w-5 h-5 text-[#F97316] shrink-0 mt-0.5" />
          <p className="text-sm text-[#7C2D12] leading-relaxed">{c.lawyer_notice}</p>
        </div>

        <div className="mt-10 space-y-8">
          {c.sections.map((s, i) => (
            <section key={i} data-testid={`contract-article-${i + 1}`}>
              <h2 className="text-lg font-bold tracking-tight text-[#0A0A0A]">{s.heading}</h2>
              <div className="mt-2 space-y-2.5">
                {s.body.map((p, j) => (
                  <p key={j} className="text-[15px] leading-relaxed text-[#27272A]">{p}</p>
                ))}
              </div>
            </section>
          ))}
        </div>

        <p className="mt-12 text-sm text-[#52525B]">{c.signature.date_place}</p>
        <div className="mt-8 grid grid-cols-2 gap-10">
          <div>
            <div className="text-sm font-bold text-[#0A0A0A]">{c.signature.photographer}</div>
            <div className="mt-12 border-t border-[#A1A1AA]" />
          </div>
          <div>
            <div className="text-sm font-bold text-[#0A0A0A]">{c.signature.platform}</div>
            <div className="mt-12 border-t border-[#A1A1AA]" />
          </div>
        </div>
      </motion.article>
    </div>
  );
}
